// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseDatePicker, BaseDynamicSelect, BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { jobActions } from '~/thunks/job/jobSlice';
import { getListUser } from '~/thunks/user/userThunk';
import { getListOrder } from '~/thunks/order/orderThunk';
import { getListTruck } from '~/thunks/truck/truckThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListTrailer } from '~/thunks/trailer/trailerThunk';
import { IQueryParamUser } from '~/utils/interface/user';
import { IParamQueryOrder } from '~/utils/interface/order';
import { IQueryParamTrailer } from '~/utils/interface/trailer';
import { RoleEnum, TypeJobDetailEnum } from '~/utils/enum/common';
import { defaultValuesJobForm, schema } from './helper';
import { IOptionTruck, IQueryParamTruck } from '~/utils/interface/truck';
import { createJob, getDetailJob, updateJob } from '~/thunks/job/jobThunk';
import { IDetailJob, IFormJob, IParamQueryJobDetail } from '~/utils/interface/jobs';
import { IBaseOption, IBodyUpdate, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { cleanObject, compareDataUpdate, getFullName, removeEmptyObjects } from '~/utils/helper/common';
import { DEFAULT_LIMIT_PAGE, DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './JobForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idJob?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const JobForm = (props: Props) => {
  //#region Destructuring Props
  const { idJob, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormJob>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesJobForm,
  });

  const { trailerType } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormJob>(defaultValuesJobForm);

  const [trailerOptionDefault, setTrailerOptionDefault] = useState<IBaseOption>();
  const [truckOptionDefault, setTruckOptionDefault] = useState<IBaseOption>();
  const [driverOptionDefault, setDriverOptionDefault] = useState<IBaseOption>();
  const [foremanOptionDefault, setForemanOptionDefault] = useState<IBaseOption>();
  const [crewOptionDefault, setCrewOptionDefault] = useState<IBaseOption[]>();
  const [loaderOptionDefault, setLoaderOptionDefault] = useState<IBaseOption[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idJob) return;

    const params: IParamQueryJobDetail = {
      jobId: idJob,
      type: TypeJobDetailEnum.OVERVIEW,
    };

    handleGetDetailJob(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idJob]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailJob = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data as IDetailJob;
        const body: IFormJob = {
          title: remainingData?.title || undefined,
          orderId: remainingData?.orderId || undefined,
          startDate: remainingData?.startDate || undefined,
          startTime: remainingData?.startTime || undefined,
          endDate: remainingData?.endDate || undefined,
          endTime: remainingData?.endTime || undefined,
          trailerId: remainingData?.trailer?.id || undefined,
          driverId: remainingData?.driver?.id || undefined,
          truckId: remainingData?.truck?.id || undefined,
          foremanId: remainingData?.foreman?.id || undefined,
          crewIds: remainingData?.crews?.map((item) => item.id || EMPTY_STRING) || [],
          loaderId: remainingData?.loader?.map((item) => item.id || EMPTY_STRING) || [],
        };

        if (remainingData?.trailer) {
          setTrailerOptionDefault({
            label: remainingData.trailer?.name,
            value: remainingData.trailer?.id,
          });
        }

        if (remainingData?.truck) {
          setTruckOptionDefault({
            label: remainingData.truck?.name,
            value: remainingData.truck?.id,
          });
        }

        if (remainingData?.driver && remainingData?.driver?.id) {
          setDriverOptionDefault({
            label: getFullName(remainingData.driver),
            value: remainingData.driver?.id,
          });
        }

        if (remainingData?.foreman && remainingData?.foreman?.id) {
          setForemanOptionDefault({
            label: getFullName(remainingData.foreman),
            value: remainingData.foreman?.id,
          });
        }

        if (remainingData?.crews && remainingData?.crews?.length > 0) {
          setCrewOptionDefault(
            remainingData?.crews?.map((item) => ({
              label: getFullName(item),
              value: item.id || EMPTY_STRING,
            }))
          );
        }

        if (remainingData?.loader && remainingData?.loader?.length > 0) {
          setLoaderOptionDefault(
            remainingData?.loader?.map((item) => ({
              label: getFullName(item),
              value: item.id || EMPTY_STRING,
            }))
          );
        }

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormJob = (data: IFormJob) => {
    if (idJob) {
      handleEditJob(data);
    } else {
      handleAddJob(data);
    }

    setIsLoading(true);
  };

  const handleAddJob = (data: IFormJob) => {
    const { trailerType, ...remainData } = data;

    const convertData = removeEmptyObjects(remainData) as IFormJob;

    dispatch(createJob(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditJob = async (data: IFormJob) => {
    const { trailerType, ...remainData } = data;

    const dataChange = compareDataUpdate(initialData, remainData) as IFormJob;

    const convertData = {
      ...dataChange,
      crewIds: data.crewIds || [],
      loaderId: data.loaderId || [],
    };

    const params: IBodyUpdate<IFormJob> = {
      id: idJob || EMPTY_STRING,
      body: cleanObject(convertData, { removeEmptyStrings: true }),
    };

    dispatch(updateJob(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const fetchOptionUser = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.CREW,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionOrder = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IParamQueryOrder = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListOrder(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.orderNumber,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionForeman = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.FOREMAN,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionLoader = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.LOADER,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionTruck = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionTruck>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamTruck = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListTruck(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.name,
        trailerType: item?.trailerType,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionTrailer = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamTrailer = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        trailerType: trailerType || [],
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListTrailer(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    [trailerType]
  );

  const fetchOptionDriver = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.DRIVER,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='jobFormModalComponent'
      aria-labelledby='jobFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='jobFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t(idJob ? 'job_form_title_edit_job' : 'job_form_title_add_job')} styleType='bigTitle' />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'title'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    label={t('job_form_title')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.title?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            {/* <Controller
              name={'orderId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  height={37}
                  styleType='contained'
                  value={value}
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  disabled={!!idJob}
                  label={t('job_form_title_order')}
                  callApiFn={fetchOptionOrder}
                  errorMessage={errors.orderId?.message}
                />
              )}
            /> */}
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name={'loaderId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseDynamicSelect
                  isSearch
                  multiple={true}
                  gap={8}
                  label={t('job_form_title_loader')}
                  callApiFn={fetchOptionLoader}
                  defaultSelected={loaderOptionDefault}
                  value={value}
                  onChange={(selected) => {
                    onChange(selected.optionsSelected.map((opt) => opt.value));
                  }}
                  itemNameDefault={loaderOptionDefault?.map((item) => item.label)}
                  errorMessage={errors.loaderId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name={'foremanId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  styleType='contained'
                  value={value}
                  defaultSelected={foremanOptionDefault}
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  label={t('job_form_title_foreman')}
                  callApiFn={fetchOptionForeman}
                  itemNameDefault={foremanOptionDefault?.label}
                  errorMessage={errors.foremanId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='crewIds'
              render={({ field: { value, onChange } }) => {
                return (
                  <BaseDynamicSelect
                    isSearch
                    multiple={true}
                    gap={8}
                    label={t('job_form_title_crew')}
                    callApiFn={fetchOptionUser}
                    defaultSelected={crewOptionDefault}
                    value={value}
                    onChange={(selected) => {
                      onChange(selected.optionsSelected.map((opt) => opt.value));
                    }}
                    itemNameDefault={crewOptionDefault?.map((item) => item.label)}
                    errorMessage={errors.crewIds?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name={'driverId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  styleType='contained'
                  value={value}
                  defaultSelected={driverOptionDefault}
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  label={t('job_form_title_driver')}
                  callApiFn={fetchOptionDriver}
                  itemNameDefault={driverOptionDefault?.label}
                  errorMessage={errors.driverId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name={'truckId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  styleType='contained'
                  value={value}
                  defaultSelected={truckOptionDefault}
                  onChange={(data: any) => {
                    onChange(data?.value);
                  }}
                  label={t('job_form_title_truck')}
                  callApiFn={fetchOptionTruck}
                  itemNameDefault={truckOptionDefault?.label}
                  errorMessage={errors.truckId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name={'trailerId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={8}
                  styleType='contained'
                  defaultSelected={trailerOptionDefault}
                  value={value}
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  label={t('job_form_title_trailer')}
                  callApiFn={fetchOptionTrailer}
                  itemNameDefault={trailerOptionDefault?.label}
                  errorMessage={errors.trailerId?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='startDate'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker
                  label={t('job_form_title_start_date')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.startDate?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='endDate'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker
                  label={t('job_form_title_end_date')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.endDate?.message}
                />
              )}
            />
          </Grid2>

          {/*
          <Grid2 size={6}>
            <Controller
              control={control}
              name='startTime'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker
                  label={t('job_form_title_start_time')}
                  mode='time'
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.startTime?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='endTime'
              render={({ field: { onChange, value } }) => (
                <BaseDatePicker
                  label={t('job_form_title_end_time')}
                  mode='time'
                  height={36}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.endTime?.message}
                />
              )}
            />
          </Grid2> */}
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormJob)}
          disabled={idJob ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default JobForm;
